import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, Database, Trash2, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

// Get default dates (last 3 days)
const getDefaultDates = () => {
  const today = new Date();
  const threeDaysAgo = new Date(today);
  threeDaysAgo.setDate(today.getDate() - 3);
  
  return {
    from: threeDaysAgo.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0]
  };
};

export const ImportTab = ({ 
  stores, 
  pgStores,
  importStoreId,
  setImportStoreId,
  onImportSuccess,
  salesImportHistory = [],
  onDeleteImport,
}) => {
  const [uploadingSales, setUploadingSales] = useState(false);
  const [importingSales, setImportingSales] = useState(false);
  const [importingFromDb, setImportingFromDb] = useState(false);
  const [lastImportResult, setLastImportResult] = useState(null);
  const salesFileRef = useRef(null);
  
  // Date selection for database import
  const defaultDates = getDefaultDates();
  const [dbDateFrom, setDbDateFrom] = useState(defaultDates.from);
  const [dbDateTo, setDbDateTo] = useState(defaultDates.to);

  // Import sales file
  const importSalesFile = async (file) => {
    if (!file) return;
    
    const storeName = stores.find(s => s.id === importStoreId)?.name || importStoreId;
    if (!window.confirm(`Confirma a importação para a loja "${storeName}"?`)) {
      if (salesFileRef.current) salesFileRef.current.value = '';
      return;
    }
    
    setUploadingSales(true);
    setImportingSales(true);
    setLastImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("store_id", importStoreId);
    
    try {
      const response = await axios.post(`${API}/sales/import-file`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        const result = {
          success: true,
          imported: response.data.imported_count || 0,
          duplicates: response.data.duplicate_count || 0,
          total: (response.data.imported_count || 0) + (response.data.duplicate_count || 0),
          source: 'csv',
          storeName: response.data.store_name
        };
        setLastImportResult(result);
        
        const msg = result.imported > 0 
          ? `✅ ${result.imported} registros importados!`
          : `ℹ️ Nenhum registro novo encontrado.`;
        toast.success(msg);
        onImportSuccess?.();
      } else {
        toast.error(response.data.message || "Erro ao importar");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar vendas");
    } finally {
      setUploadingSales(false);
      setImportingSales(false);
      if (salesFileRef.current) salesFileRef.current.value = '';
    }
  };

  // Import from PostgreSQL database
  const importSalesFromDatabase = async () => {
    const pgStore = pgStores.find(s => s.id === importStoreId);
    if (!pgStore) {
      toast.error(`Loja "${importStoreId}" não tem acesso ao banco de dados configurado`);
      return;
    }

    // Validate dates
    if (!dbDateFrom || !dbDateTo) {
      toast.error("Selecione as datas de início e fim");
      return;
    }

    if (new Date(dbDateFrom) > new Date(dbDateTo)) {
      toast.error("Data inicial não pode ser maior que a data final");
      return;
    }

    const storeName = stores.find(s => s.id === importStoreId)?.name || importStoreId;
    const dateRange = `${new Date(dbDateFrom).toLocaleDateString('pt-BR')} a ${new Date(dbDateTo).toLocaleDateString('pt-BR')}`;
    
    if (!window.confirm(`Confirma a importação do período ${dateRange} do banco de dados para "${storeName}"?`)) {
      return;
    }

    setImportingFromDb(true);
    setLastImportResult(null);
    
    try {
      const params = new URLSearchParams();
      params.append("store_id", importStoreId);
      params.append("date_from", dbDateFrom);
      params.append("date_to", dbDateTo);
      
      const response = await axios.post(`${API}/sales/import-from-database?${params.toString()}`);
      
      if (response.data.success) {
        const result = {
          success: true,
          imported: response.data.imported || 0,
          duplicates: response.data.duplicates || 0,
          total: response.data.total_fetched || 0,
          source: 'postgresql',
          dateRange: response.data.date_range
        };
        setLastImportResult(result);
        
        const msg = result.imported > 0 
          ? `✅ ${result.imported} registros importados!`
          : `ℹ️ Nenhum registro novo encontrado.`;
        toast.success(msg);
        onImportSuccess?.();
      } else {
        toast.error(response.data.message || "Erro ao importar");
      }
    } catch (error) {
      console.error("Import from DB error:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar do banco de dados");
    } finally {
      setImportingFromDb(false);
    }
  };

  // Quick date presets
  const setDatePreset = (days) => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - days);
    setDbDateFrom(fromDate.toISOString().split('T')[0]);
    setDbDateTo(today.toISOString().split('T')[0]);
  };

  const hasPgConfig = pgStores.some(s => s.id === importStoreId);

  return (
    <Card className="shadow-sm border-slate-200">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Upload className="w-5 h-5 text-emerald-600" />
          Importar Dados de Vendas
        </CardTitle>
        <CardDescription>
          Faça upload de um arquivo CSV ou importe direto do banco de dados
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Card */}
          <div className="space-y-6">
            {/* Store Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Selecione a Loja *
              </label>
              <select
                value={importStoreId}
                onChange={(e) => setImportStoreId(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              >
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>

            {/* CSV Upload Section */}
            <div className="p-6 border-2 border-dashed border-slate-300 rounded-xl bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-400 transition-colors text-center">
              <input
                type="file"
                accept=".csv"
                ref={salesFileRef}
                onChange={(e) => importSalesFile(e.target.files[0])}
                className="hidden"
              />
              <Upload className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              <Button
                onClick={() => salesFileRef.current?.click()}
                disabled={uploadingSales || importingSales || importingFromDb}
                className="w-full max-w-xs h-11 bg-emerald-600 hover:bg-emerald-700"
              >
                {(uploadingSales || importingSales) ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando CSV...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Arquivo CSV
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                Clique para selecionar um arquivo CSV
              </p>
            </div>
            
            {/* Database Import Section */}
            {hasPgConfig && (
              <div className="p-6 border border-blue-200 rounded-xl bg-gradient-to-br from-blue-50 to-white">
                <div className="flex items-center gap-2 mb-4">
                  <Database className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-800">Importar do Banco de Dados</h3>
                </div>
                
                {/* Date Selection */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Data Inicial
                    </label>
                    <Input
                      type="date"
                      value={dbDateFrom}
                      onChange={(e) => setDbDateFrom(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Data Final
                    </label>
                    <Input
                      type="date"
                      value={dbDateTo}
                      onChange={(e) => setDbDateTo(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                
                {/* Quick Date Presets */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDatePreset(1)}
                    className="h-7 text-xs"
                  >
                    Ontem
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDatePreset(3)}
                    className="h-7 text-xs"
                  >
                    Últimos 3 dias
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDatePreset(7)}
                    className="h-7 text-xs"
                  >
                    Última semana
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDatePreset(30)}
                    className="h-7 text-xs"
                  >
                    Último mês
                  </Button>
                </div>
                
                <Button
                  onClick={importSalesFromDatabase}
                  disabled={importingFromDb || uploadingSales || importingSales}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700"
                >
                  {importingFromDb ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importando do Banco...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Importar do Banco de Dados
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Last Import Result */}
            {lastImportResult && (
              <div className={`p-4 rounded-xl border ${
                lastImportResult.imported > 0 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-3">
                  {lastImportResult.imported > 0 ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${
                      lastImportResult.imported > 0 ? 'text-emerald-800' : 'text-amber-800'
                    }`}>
                      Resultado da Importação
                    </h4>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Total de registros:</span>
                        <span className="font-medium text-slate-800">{lastImportResult.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-600">Novos importados:</span>
                        <span className="font-bold text-emerald-700">{lastImportResult.imported}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-600">Duplicados ignorados:</span>
                        <span className="font-bold text-amber-700">{lastImportResult.duplicates}</span>
                      </div>
                      {lastImportResult.dateRange && (
                        <div className="flex justify-between pt-1 border-t border-slate-200">
                          <span className="text-slate-500">Período:</span>
                          <span className="text-slate-700">
                            {lastImportResult.dateRange.start} a {lastImportResult.dateRange.end}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info & History */}
          <div className="space-y-4">
            {/* Format Info */}
            <div className="p-4 border border-slate-200 rounded-xl bg-white">
              <h3 className="font-semibold text-slate-800 mb-3">Formato CSV Esperado</h3>
              <p className="text-sm text-slate-600 mb-2">O arquivo deve conter as colunas:</p>
              <div className="flex flex-wrap gap-1">
                {['Pedido', 'Emissão', 'Cód. Barra', 'Produto', 'Qtd', 'Total Produtos', 'Desconto', 'Total Pedido'].map((col, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">{col}</span>
                ))}
              </div>
              <p className="text-xs text-amber-600 mt-3">
                ⚠️ Duplicados são detectados automaticamente pelo hash (pedido + código + produto)
              </p>
            </div>

            {/* Import History */}
            <div className="p-4 border border-slate-200 rounded-xl bg-white">
              <h3 className="font-semibold text-slate-800 mb-3">Histórico de Importações</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {salesImportHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhuma importação realizada</p>
                ) : salesImportHistory.slice(0, 15).map((imp, i) => (
                  <div key={imp.id || i} className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {imp.source === 'postgresql' ? (
                          <Database className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        ) : (
                          <Upload className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        )}
                        <p className="text-sm text-slate-700 truncate">
                          {imp.filename || (imp.source === 'postgresql' ? 'Importação do Banco' : 'Importação')}
                        </p>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {imp.created_at ? new Date(imp.created_at).toLocaleString('pt-BR') : ''}
                        {imp.store_name && <span className="ml-2 text-blue-500">• {imp.store_name}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <span className="text-xs text-emerald-600 font-semibold block">
                          +{imp.imported_count || imp.imported || imp.new_records || 0}
                        </span>
                        {(imp.duplicate_count || imp.duplicates) > 0 && (
                          <span className="text-[10px] text-amber-500">
                            {imp.duplicate_count || imp.duplicates} dup.
                          </span>
                        )}
                      </div>
                      {onDeleteImport && (
                        <button
                          onClick={() => onDeleteImport(imp.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                          title="Excluir importação"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ImportTab;
